/**
 * VerifyIdentity.jsx
 * Driver's license + insurance card upload flow with status tracker.
 * Calls POST /users/me/verify to get pre-signed S3 PUT URLs,
 * then uploads directly to S3.
 */
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '../context/AuthContext';

const STATUS_LABELS = {
  unverified:     { label: 'Not Started',       color: 'bg-gray-100 text-gray-600' },
  pending_upload: { label: 'Uploading…',         color: 'bg-yellow-100 text-yellow-700' },
  pending_admin:  { label: 'Under Review',       color: 'bg-blue-100 text-blue-700' },
  verified:       { label: 'Verified ✓',         color: 'bg-green-100 text-green-700' },
  rejected:       { label: 'Rejected',           color: 'bg-red-100 text-red-700' },
};

export default function VerifyIdentity() {
  const api = useApi();
  const [searchParams] = useSearchParams();
  const fromPrivateRentals = searchParams.get('reason') === 'private-rentals';

  const [status, setStatus]         = useState(null);
  const [dlFile, setDlFile]         = useState(null);
  const [insFile, setInsFile]       = useState(null);
  const [uploading, setUploading]   = useState(false);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');

  // Load current verification status
  useEffect(() => {
    api.get('/users/me/verify/status')
      .then(r => setStatus(r.data))
      .catch(e => console.error('Failed to load verification status', e));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');

    if (!dlFile || !insFile) {
      setError('Please select both your driver\'s license and insurance card.');
      return;
    }

    setUploading(true);
    try {
      // 1. Get pre-signed upload URLs
      const { data } = await api.post('/users/me/verify', {
        dl_filename:        dlFile.name,
        dl_content_type:    dlFile.type || 'image/jpeg',
        ins_filename:       insFile.name,
        ins_content_type:   insFile.type || 'image/jpeg',
      });

      // 2. Upload DL directly to S3
      await fetch(data.dl_upload_url, {
        method:  'PUT',
        body:    dlFile,
        headers: { 'Content-Type': dlFile.type || 'image/jpeg' },
      });

      // 3. Upload insurance card directly to S3
      await fetch(data.insurance_upload_url, {
        method:  'PUT',
        body:    insFile,
        headers: { 'Content-Type': insFile.type || 'image/jpeg' },
      });

      setSuccess('Documents uploaded successfully! Our team will review them shortly (usually within 24 hours).');
      setStatus(prev => ({ ...prev, verificationStatus: 'pending_admin' }));
    } catch (err) {
      setError('Upload failed. Please try again or contact support.');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const currentStatus = status?.verificationStatus || 'unverified';
  const statusInfo    = STATUS_LABELS[currentStatus] || STATUS_LABELS.unverified;
  const isVerified    = currentStatus === 'verified';
  const isPending     = currentStatus === 'pending_admin';

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Identity Verification</h1>
        <p className="text-gray-500 mb-8">
          Required before your first booking. Upload your driver's license and proof of insurance.
        </p>

        {/* Contextual banner when redirected from Private Rentals gate */}
        {fromPrivateRentals && !isVerified && !isPending && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex gap-3">
            <span className="text-2xl">🔒</span>
            <div>
              <p className="text-sm font-semibold text-blue-800">Verification required for Private Rentals</p>
              <p className="text-sm text-blue-700 mt-0.5">
                Submit your documents below. Once our team approves them you'll get access to the Private Rentals page to book directly with Drive Dobias.
              </p>
            </div>
          </div>
        )}

        {/* Status Badge */}
        <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-8 ${statusInfo.color}`}>
          {statusInfo.label}
        </div>

        {isVerified && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8">
            <h2 className="text-lg font-semibold text-green-800 mb-1">You're verified!</h2>
            <p className="text-green-700 text-sm">
              Your identity has been verified. You can now book any vehicle.
            </p>
            <a href="/" className="mt-4 inline-block text-blue-600 hover:underline text-sm">
              Browse available vehicles →
            </a>
          </div>
        )}

        {isPending && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
            <h2 className="text-lg font-semibold text-blue-800 mb-1">Documents under review</h2>
            <p className="text-blue-700 text-sm">
              We've received your documents and are reviewing them. You'll receive an email once approved (usually within 24 hours).
            </p>
          </div>
        )}

        {!isVerified && !isPending && (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">

            {/* Driver's License */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Driver's License <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-400 mb-3">
                Clear photo of the front of your license. JPG, PNG, or PDF.
              </p>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={e => setDlFile(e.target.files[0])}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {dlFile && (
                <p className="mt-1 text-xs text-green-600">✓ {dlFile.name}</p>
              )}
            </div>

            {/* Insurance Card */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Proof of Insurance <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-400 mb-3">
                Insurance card or declaration page showing your name and policy number.
              </p>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={e => setInsFile(e.target.files[0])}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {insFile && (
                <p className="mt-1 text-xs text-green-600">✓ {insFile.name}</p>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                {error}
              </div>
            )}
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
                {success}
              </div>
            )}

            <button
              type="submit"
              disabled={uploading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {uploading ? 'Uploading…' : 'Submit Documents'}
            </button>

            <p className="text-xs text-gray-400 text-center">
              Your documents are stored securely and only reviewed by Drive Dobias staff.
            </p>
          </form>
        )}

        {/* Extracted info (if available) */}
        {status && (status.dlExpiryDate || status.insurancePolicyNum) && (
          <div className="mt-6 bg-gray-50 rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Extracted Information</h3>
            <dl className="space-y-1 text-sm">
              {status.dlExpiryDate && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">DL Expiry</dt>
                  <dd className="font-medium">{status.dlExpiryDate}</dd>
                </div>
              )}
              {status.insurancePolicyNum && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Policy #</dt>
                  <dd className="font-medium">{status.insurancePolicyNum}</dd>
                </div>
              )}
              {status.insuranceExpiryDate && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Insurance Expiry</dt>
                  <dd className="font-medium">{status.insuranceExpiryDate}</dd>
                </div>
              )}
            </dl>
          </div>
        )}
      </div>
    </div>
  );
}
